// App-Shell: Header mit Produkt-Wordmark + Navigation, darunter die Routes.
// Bewusst schlicht — nicht die Marketing-Seite nachbauen, sondern ein
// Admin-Tool, das sich der Marke unterordnet.

import { NavLink, Outlet } from "react-router-dom";

import { Logo } from "@/components/Logo";
import { useCurrentUser, useHealth, useLogout } from "@/api/hooks";
import { useToast } from "@/components/Toast";

import "./Layout.css";

export function Layout() {
  const health = useHealth();
  return (
    <div className="bmd-app">
      <header className="bmd-header">
        <div className="bmd-header-inner">
          <div className="bmd-header-left">
            <Logo variant="header" />
          </div>
          <nav className="bmd-nav">
            <NavLink to="/" end className="bmd-nav-link">
              Dashboard
            </NavLink>
            <NavLink to="/upload" className="bmd-nav-link">
              Upload
            </NavLink>
            <NavLink to="/mandanten" className="bmd-nav-link">
              Mandanten
            </NavLink>
            <NavLink to="/admin" className="bmd-nav-link">
              Cockpit
            </NavLink>
            <NavLink to="/einstellungen" className="bmd-nav-link">
              Profil
            </NavLink>
          </nav>
          <div className="bmd-header-right">
            <HealthPill health={health.data} loading={health.isLoading} />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="bmd-main">
        <Outlet />
      </main>

      <footer className="bmd-footer">
        <div className="bmd-footer-inner">
          Fidena · Kanzlei-Automatisierung
        </div>
      </footer>
    </div>
  );
}

function UserMenu() {
  const me = useCurrentUser();
  const logout = useLogout();
  const toast = useToast();

  const user = me.data;
  if (!user) return null;

  const displayName = user.full_name || user.email;
  const initials = displayName
    .split(/\s+/)
    .map((part: string) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="bmd-user-menu" title={user.email}>
      <span className="bmd-user-avatar" aria-hidden="true">
        {initials || "?"}
      </span>
      <span className="bmd-user-name">{displayName}</span>
      <button
        type="button"
        className="bmd-user-logout"
        onClick={() =>
          logout.mutate(undefined, {
            onSuccess: () => toast.info("Abgemeldet."),
            onError: (err) =>
              toast.error(`Abmelden fehlgeschlagen: ${err.message}`),
          })
        }
        disabled={logout.isPending}
        title="Abmelden"
        aria-label="Abmelden"
      >
        <LogoutIcon />
      </button>
    </div>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}


function HealthPill({
  health,
  loading,
}: {
  health: ReturnType<typeof useHealth>["data"];
  loading: boolean;
}) {
  if (loading || !health) {
    return <span className="bmd-health-pill bmd-health-pill-muted">…</span>;
  }
  const ok = health.status === "ok";
  return (
    <div
      className={`bmd-health-pill ${
        ok ? "bmd-health-pill-ok" : "bmd-health-pill-warn"
      }`}
      title={`Cloud-API: ${health.service} (${health.status})`}
    >
      <span className="bmd-health-dot" aria-hidden="true" />
      <span>{ok ? "Cloud verbunden" : "Cloud gestört"}</span>
    </div>
  );
}
